import React, { useState } from "react";
import {
  Table,
  Header,
  TableProps,
  Popover,
  Button,
  KeyValuePairs,
  KeyValuePairsProps,
  Link,
  Icon,
  SpaceBetween,
  Toggle,
} from "@amzn/awsui-components-console";
import { FixedFeeConfig } from "@amzn/fuse-accounting-central-datastore-client";
import { NumericValue } from "@smithy/core/serde";
import { RegionalDomain } from "src/config/BartLambdaConfig";
import Decimal from "decimal.js";
import { getTransactionInfo } from "src/pages/commons/transaction-info";
import { isFxApplicable, formatWithFx } from "src/utils/fx-helper";

interface Props {
  fixedFeeConfig: FixedFeeConfig;
  domain: RegionalDomain;
}

const DUMMY_COLUMN_DEFINITION = {
  id: "",
  header: "",
  cell: () => "",
};

// Helper function to format numeric values
const formatNumber = (value: any, decimalPlaces = 2) => {
  if (!value)
    return <span style={{ display: "block", textAlign: "right" }}>-</span>;
  return (
    <span style={{ display: "block", textAlign: "right" }}>
      {Number(
        value instanceof NumericValue ? value.string : value.toString(),
      ).toLocaleString("en-US", {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
      })}
    </span>
  );
};

export const AdjustmentsTable: React.FC<Props> = ({
  fixedFeeConfig,
  domain,
}) => {
  const hasFx = isFxApplicable(fixedFeeConfig);
  const [fxApplied, setFxApplied] = useState(false);

  const adjustmentCount = fixedFeeConfig.adjustments
    ? Object.keys(fixedFeeConfig.adjustments).length
    : 0;
  const rebalanceCount = fixedFeeConfig.rebalance
    ? Object.keys(fixedFeeConfig.rebalance).length
    : 0;

  // Render only when there is at least one adjustment or rebalance to show.
  if (adjustmentCount === 0 && rebalanceCount === 0) {
    return null;
  }

  const fxEnabled = hasFx && fxApplied;
  const currencyLabel = fxEnabled
    ? fixedFeeConfig.billingCurrencyCode!
    : fixedFeeConfig.contractCurrencyCode!;

  // Helper to format a number, replacing with FX-converted value when FX is enabled
  const fxFormatNumber = (value: any, decimalPlaces = 2) => {
    if (fxEnabled && value) {
      const converted = formatWithFx(
        value,
        fixedFeeConfig.fxRate,
        fixedFeeConfig.billingCurrencyCode,
      );
      if (converted) {
        return (
          <span style={{ display: "block", textAlign: "right" }}>
            {converted}
          </span>
        );
      }
    }
    return formatNumber(value, decimalPlaces);
  };

  // Helper to format colored amounts (absolute value with sign indicated by color)
  const formatColoredAmount = (value: any, label?: string) => {
    if (!value)
      return <span style={{ display: "block", textAlign: "right" }}>-</span>;
    const numValue =
      typeof value === "object" && value.string
        ? Number(value.string)
        : Number(value);

    if (numValue === 0)
      return <span style={{ display: "block", textAlign: "right" }}>0.00</span>;

    const color = numValue > 0 ? "#00802f" : "#db0000";
    const absValue = Math.abs(numValue);
    const displayValue =
      (fxEnabled &&
        formatWithFx(
          absValue,
          fixedFeeConfig.fxRate,
          fixedFeeConfig.billingCurrencyCode,
        )) ||
      absValue.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

    if (label) {
      return (
        <span
          style={{
            color,
            display: "flex",
            justifyContent: "flex-end",
            gap: "4px",
          }}
        >
          <span>{displayValue}</span>
          <span style={{ display: "inline-block", width: "80px" }}>
            ({label})
          </span>
        </span>
      );
    }

    return (
      <span style={{ color, display: "block", textAlign: "right" }}>
        {displayValue}
      </span>
    );
  };

  // Helper to get transaction items for KeyValuePairs
  const getTransactionItems = (externalId: string, transactionType: string) => {
    const transactionInfo = getTransactionInfo(
      externalId,
      transactionType,
      fixedFeeConfig.environment === "TEST",
      domain,
    );
    return [
      {
        label: "Transaction(s)",
        value: (
          <Link external href={`/transactionsearch/external_id/${externalId}`}>
            {externalId}
          </Link>
        ),
      },
      {
        label: "FuseBillingService Workflows",
        value: (
          <span>
            <Link external href={transactionInfo.fuse.transactionWf?.link}>
              Transaction WF
            </Link>
            ,&nbsp;
            <Link external href={transactionInfo.fuse.fusenWf.link}>
              FUSEN WF
            </Link>
          </span>
        ),
      },
      {
        label: "Other Links",
        value: (
          <span>
            <Link external href={transactionInfo.acid.link}>
              ACID
            </Link>
            ,&nbsp;
            <Link external href={transactionInfo.flash.link}>
              FLASH
            </Link>
          </span>
        ),
      },
    ];
  };

  // Helper to get issuance transaction links popover
  const getIssuanceTransactionPopover = () => {
    const issuanceExternalId = `${fixedFeeConfig.fixedFeeId}.Payment`;
    const groups: KeyValuePairsProps.Group[] = [
      {
        type: "group",
        title: "Issuance Transaction",
        items: getTransactionItems(issuanceExternalId, "CHARGE"),
      },
    ];

    return (
      <Popover
        renderWithPortal
        size="large"
        position="top"
        triggerType="custom"
        dismissButton={false}
        content={<KeyValuePairs columns={1} items={groups} />}
      >
        <Button variant="inline-link">Expand</Button>
      </Popover>
    );
  };

  // Helper to get adjustment transaction links popover
  const getAdjustmentTransactionPopover = (
    item: any,
    executionIndex: number,
  ) => {
    if (
      !item.adjustmentAmount &&
      !item.receivableShiftAmount &&
      !item.deferralRevenueShiftAmount
    ) {
      return <span>-</span>;
    }

    const adjustmentAmount = item.adjustmentAmount
      ? typeof item.adjustmentAmount === "object" &&
        item.adjustmentAmount.string
        ? Number(item.adjustmentAmount.string)
        : Number(item.adjustmentAmount)
      : 0;

    const receivableShiftAmount = item.receivableShiftAmount
      ? typeof item.receivableShiftAmount === "object" &&
        item.receivableShiftAmount.string
        ? Number(item.receivableShiftAmount.string)
        : Number(item.receivableShiftAmount)
      : 0;

    const deferralRevenueShiftAmount = item.deferralRevenueShiftAmount
      ? typeof item.deferralRevenueShiftAmount === "object" &&
        item.deferralRevenueShiftAmount.string
        ? Number(item.deferralRevenueShiftAmount.string)
        : Number(item.deferralRevenueShiftAmount)
      : 0;

    const groups: KeyValuePairsProps.Group[] = [];

    // Adjustment transaction
    if (adjustmentAmount !== 0) {
      const isTrueUp = adjustmentAmount > 0;
      const adjustmentExternalId = `${fixedFeeConfig.fixedFeeId}-${executionIndex}.${isTrueUp ? "TrueUp" : "TrueDown"}`;

      groups.push({
        type: "group",
        title: "Adjustment Transaction",
        items: getTransactionItems(adjustmentExternalId, "TRANSACTION"),
      });
    }

    // SubBalanceShift transaction
    if (receivableShiftAmount !== 0 || deferralRevenueShiftAmount !== 0) {
      const subBalanceShiftExternalId = `${fixedFeeConfig.fixedFeeId}-${executionIndex}.SubBalShift`;

      groups.push({
        type: "group",
        title: "SubBalanceShift Transaction",
        items: getTransactionItems(subBalanceShiftExternalId, "CHARGE"),
      });
    }

    return (
      <Popover
        renderWithPortal
        size="large"
        position="top"
        triggerType="custom"
        dismissButton={false}
        content={<KeyValuePairs columns={groups.length} items={groups} />}
      >
        <Button variant="inline-link">Expand</Button>
      </Popover>
    );
  };

  // Helper to format the carve split ratio effective date (an ISO instant) as its full timestamp.
  const formatEffectiveTimestamp = (value: any): string => {
    if (!value) return "-";
    return value instanceof Date ? value.toISOString() : String(value);
  };

  // Helper to extract the date portion (YYYY-MM-DD) from a Date or ISO instant string.
  const formatEffectiveDate = (value: any): string => {
    if (!value) return "-";
    const iso = value instanceof Date ? value.toISOString() : String(value);
    return iso.split("T")[0];
  };

  // Helper to get rebalance transaction links popover. The rebalance charge external id matches
  // FuseFixedFeeService's FBGSAccessor.postRebalanceCharge: `${fixedFeeId}-${yearMonth}-r${revision}.Rebalance`.
  // A charge is only posted once revision >= 1; absent/0 means nothing has been posted yet.
  const getRebalanceTransactionPopover = (item: any) => {
    if (!item.rebalanceRevision) {
      return <span>-</span>;
    }

    const rebalanceExternalId = `${fixedFeeConfig.fixedFeeId}-${item.rebalanceYearMonth}-r${item.rebalanceRevision}.Rebalance`;

    const groups: KeyValuePairsProps.Group[] = [
      {
        type: "group",
        title: "Rebalance Transaction",
        items: [
          {
            label: "Carve Split Ratio Effective Date",
            value: formatEffectiveTimestamp(item.carveSplitRatioEffectiveDate),
          },
          ...getTransactionItems(rebalanceExternalId, "CHARGE"),
        ],
      },
    ];

    return (
      <Popover
        renderWithPortal
        size="large"
        position="top"
        triggerType="custom"
        dismissButton={false}
        content={<KeyValuePairs columns={1} items={groups} />}
      >
        <Button variant="inline-link">Expand</Button>
      </Popover>
    );
  };

  const adjustmentColumnDefinitions: TableProps.ColumnDefinition<any>[] = [
    DUMMY_COLUMN_DEFINITION,
    {
      id: "transaction",
      header: "Transaction Info",
      cell: (item: any) => {
        if (item.rowType === "rebalance") {
          return getRebalanceTransactionPopover(item);
        }
        return item.executionIndex !== undefined
          ? getAdjustmentTransactionPopover(item, item.executionIndex)
          : getIssuanceTransactionPopover();
      },
    },
    {
      id: "adjustmentType",
      header: "Adjustment Type",
      cell: (item: any) => {
        switch (item.rowType) {
          case "issuance":
            return "ISSUANCE";
          case "rebalance":
            return "REBALANCE";
          default: {
            const numValue =
              typeof item.adjustmentAmount === "object" &&
              item.adjustmentAmount?.string
                ? Number(item.adjustmentAmount.string)
                : Number(item.adjustmentAmount);
            if (numValue === 0) return "RECLASS";
            return numValue < 0 ? "RECLASS + TRUE-DOWN" : "RECLASS + TRUE-UP";
          }
        }
      },
    },
    {
      id: "adjustmentDate",
      header: "Adjustment Date",
      cell: (item: any) => item.adjustmentDate,
    },
    {
      id: "adjustingPeriod",
      header: "Adjusting Period",
      cell: (item: any) =>
        item.periodStartDate
          ? `${item.periodStartDate || "-"} to ${item.periodEndDate || "-"}`
          : "-",
    },
    {
      id: "activeBenefitCount",
      header: "Active Benefit Count",
      cell: (item: any) =>
        item.activeBenefitCount !== undefined
          ? item.activeBenefitCount.toLocaleString("en-US")
          : "-",
    },
    {
      id: "deferredRevenue",
      header: (
        <>
          Deferred Revenue
          <br />({currencyLabel})
        </>
      ),
      cell: (item: any) => fxFormatNumber(item.deferredRevenue),
    },
    {
      id: "revisedPlanPrice",
      header: (
        <>
          Revised Plan Price
          <br />({currencyLabel})
        </>
      ),
      cell: (item: any) => fxFormatNumber(item.revisedPlanPrice, 4),
    },
    {
      id: "adjustmentAmount",
      header: (
        <>
          Adjustment Amount
          <br />({currencyLabel})
        </>
      ),
      cell: (item: any) => formatColoredAmount(item.adjustmentAmount),
    },
  ];

  // The 28655 LT Deferred Revenue and 23200 Unearned Revenue columns are shared: reclass adjustments
  // populate them via the running-balance math, and rebalances populate them with their stored
  // re-carved balances. They render whenever EITHER reclassBalance or rebalance data exists. The
  // other six columns below are adjustment/reclass-only concepts, so they stay gated on reclassBalance.
  const longTermDeferredRevenueColumn: TableProps.ColumnDefinition<any> = {
    id: "longTermDeferredRevenue",
    header: (
      <>
        28655
        <br />
        LT Deferred Revenue
        <br />({currencyLabel})
      </>
    ),
    cell: (item: any) => fxFormatNumber(item.longTermDeferredRevenue),
  };
  const unearnedRevenueColumn: TableProps.ColumnDefinition<any> = {
    id: "unearnedRevenue",
    header: (
      <SpaceBetween direction="horizontal" size="xxs">
        <span>
          23200
          <br />
          Unearned Revenue
          <br />({currencyLabel})
        </span>
        <Popover
          renderWithPortal
          size="medium"
          position="top"
          triggerType="text"
          dismissButton={false}
          content={
            <SpaceBetween direction="vertical" size="xs">
              <div>
                <strong>Reclass:</strong> contractPrice -
                longTermDeferredRevenue - cumulativeRecognizedRevenue
              </div>
              <div>
                <strong>Rebalance:</strong> see the{" "}
                <Link
                  href="https://drive-render.corp.amazon.com/view/Fuse/Pascal/Fixed%20Fee/msor-ff-ard-flow-infographic.html"
                  external
                  externalIconAriaLabel="Opens in a new tab"
                >
                  MSOR FF ARD flow
                </Link>{" "}
                for how these calculations are derived.
              </div>
            </SpaceBetween>
          }
        >
          <Icon name="status-info" variant="link" />
        </Popover>
      </SpaceBetween>
    ),
    cell: (item: any) => fxFormatNumber(item.unearnedRevenue),
  };

  if (fixedFeeConfig.reclassBalance) {
    adjustmentColumnDefinitions.push(
      {
        id: "receivableShiftAmount",
        header: (
          <>
            Receivable Shift
            <br />({currencyLabel})
          </>
        ),
        cell: (item: any) => formatColoredAmount(item.receivableShiftAmount),
      },
      {
        id: "deferralRevenueShiftAmount",
        header: (
          <>
            Deferred Revenue Shift
            <br />({currencyLabel})
          </>
        ),
        cell: (item: any) =>
          formatColoredAmount(item.deferralRevenueShiftAmount),
      },
      {
        id: "cumulativeInvoiceAmount",
        header: (
          <SpaceBetween direction="horizontal" size="xxs">
            <span>
              Cumulative
              <br />
              Invoice Amount
              <br />({currencyLabel})
            </span>
            <Popover
              renderWithPortal
              size="medium"
              position="top"
              triggerType="text"
              dismissButton={false}
              content="Sum of invoiceAmount from invoiceBreakdown where invoiceDate <= adjustmentDate."
            >
              <Icon name="status-info" variant="link" />
            </Popover>
          </SpaceBetween>
        ),
        cell: (item: any) => fxFormatNumber(item.cumulativeInvoiceAmount),
      },
      {
        id: "cumulativeRecognizedRevenue",
        header: (
          <SpaceBetween direction="horizontal" size="xxs">
            <span>
              Cumulative
              <br />
              Recognized Revenue
              <br />({currencyLabel})
            </span>
            <Popover
              renderWithPortal
              size="medium"
              position="top"
              triggerType="text"
              dismissButton={false}
              content="Sum of per-period deferredRevenue (+) and adjustmentAmount (+/-) from all executed adjustments up to this date. This amount also includes revenue being amortized by rACE."
            >
              <Icon name="status-info" variant="link" />
            </Popover>
          </SpaceBetween>
        ),
        cell: (item: any) => fxFormatNumber(item.cumulativeRecognizedRevenue),
      },
      {
        id: "longTermReceivables",
        header: (
          <>
            18203
            <br />
            LT Receivables
            <br />({currencyLabel})
          </>
        ),
        cell: (item: any) => fxFormatNumber(item.longTermReceivables),
      },
      longTermDeferredRevenueColumn,
      {
        id: "unbilledAccountsReceivables",
        header: (
          <SpaceBetween direction="horizontal" size="xxs">
            <span>
              13003
              <br />
              Unbilled AR
              <br />({currencyLabel})
            </span>
            <Popover
              renderWithPortal
              size="medium"
              position="top"
              triggerType="text"
              dismissButton={false}
              content="contractPrice - longTermReceivables - cumulativeInvoiceAmount"
            >
              <Icon name="status-info" variant="link" />
            </Popover>
          </SpaceBetween>
        ),
        cell: (item: any) => fxFormatNumber(item.unbilledAccountsReceivables),
      },
      unearnedRevenueColumn,
    );
  } else if (fixedFeeConfig.rebalance) {
    // Rebalance data but no reclassBalance: show only the two columns rebalances populate.
    adjustmentColumnDefinitions.push(
      longTermDeferredRevenueColumn,
      unearnedRevenueColumn,
    );
  }

  adjustmentColumnDefinitions.push(DUMMY_COLUMN_DEFINITION);

  // Get executed adjustments with their index
  const executedAdjustmentDates = Object.entries(
    fixedFeeConfig.adjustments ?? {},
  )
    .filter(([_, adjustment]) => adjustment.adjustmentAmount !== undefined)
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([date], index) => ({ date, index: index + 1 }));

  // If reclass is enabled, calculate the LT/ST amount as iterated
  const adjustmentData = [];

  let longTermReceivables: Decimal | undefined,
    longTermDeferredRevenue: Decimal | undefined,
    unbilledAccountsReceivables: Decimal | undefined,
    unearnedRevenue: Decimal | undefined,
    cumulativeRecognizedRevenue: Decimal | undefined,
    cumulativeInvoiceAmount: Decimal | undefined;

  const contractPrice = new Decimal(
    fixedFeeConfig.contractPrice?.string ?? "0",
  );

  // Pre-sort invoice dates for cumulative invoice amount calculation
  const sortedInvoiceDates = fixedFeeConfig.invoiceBreakdown
    ? Object.entries(fixedFeeConfig.invoiceBreakdown).sort(([a], [b]) =>
        a.localeCompare(b),
      )
    : [];

  if (fixedFeeConfig.reclassBalance) {
    longTermReceivables = new Decimal(
      fixedFeeConfig.reclassBalance.longTermReceivables?.string ?? "0",
    );
    longTermDeferredRevenue = new Decimal(
      fixedFeeConfig.reclassBalance.longTermDeferredRevenue?.string ?? "0",
    );
    cumulativeRecognizedRevenue = new Decimal("0");
    cumulativeInvoiceAmount = new Decimal("0");
    unearnedRevenue = contractPrice
      .sub(longTermDeferredRevenue)
      .sub(cumulativeRecognizedRevenue);
    unbilledAccountsReceivables = contractPrice
      .sub(longTermReceivables)
      .sub(cumulativeInvoiceAmount);
  }

  adjustmentData.push({
    rowType: "issuance",
    adjustmentDate: fixedFeeConfig.dealStartDate,
    longTermReceivables,
    longTermDeferredRevenue,
    unbilledAccountsReceivables,
    unearnedRevenue,
    cumulativeRecognizedRevenue,
    cumulativeInvoiceAmount,
  });

  // Build adjustment rows. This MUST stay in chronological order so the running-balance math above
  // iterates correctly; the chronological array is reused (merged with rebalances) for display below.
  const adjustmentRows = Object.entries(fixedFeeConfig.adjustments ?? {})
    .sort(([date, _], [date2, __]) => date.localeCompare(date2))
    .map(([date, adjustment]) => {
      const executed = executedAdjustmentDates.find((e) => e.date === date);
      const data: any = {
        rowType: "adjustment",
        adjustmentDate: date,
        sortKey: date,
        periodStartDate: adjustment.periodStartDate,
        periodEndDate: adjustment.periodEndDate,
        activeBenefitCount: adjustment.activeBenefitCount,
        deferredRevenue: adjustment.deferredRevenue,
        revisedPlanPrice: adjustment.revisedPlanPrice,
        adjustmentAmount: adjustment.adjustmentAmount,
        receivableShiftAmount: adjustment.receivableShiftAmount,
        deferralRevenueShiftAmount: adjustment.deferralRevenueShiftAmount,
        executionIndex: executed ? executed.index : 0,
      };

      // only populate reclass relevant items if reclass is enabled and this occurrence has executed
      if (fixedFeeConfig.reclassBalance && adjustment.adjustmentAmount) {
        longTermReceivables = longTermReceivables!.sub(
          new Decimal(adjustment.receivableShiftAmount?.string ?? "0"),
        );
        longTermDeferredRevenue = longTermDeferredRevenue!.sub(
          new Decimal(adjustment.deferralRevenueShiftAmount?.string ?? "0"),
        );

        // cumulativeRecognizedRevenue += deferredRevenue + adjustmentAmount
        cumulativeRecognizedRevenue = cumulativeRecognizedRevenue!
          .add(new Decimal(adjustment.deferredRevenue?.string ?? "0"))
          .add(new Decimal(adjustment.adjustmentAmount?.string ?? "0"));

        // cumulativeInvoiceAmount = sum of invoiceAmounts where invoiceDate <= adjustmentDate
        cumulativeInvoiceAmount = sortedInvoiceDates
          .filter(([invoiceDate]) => invoiceDate.localeCompare(date) <= 0)
          .reduce(
            (sum, [, invoice]) =>
              sum.add(new Decimal(invoice.invoiceAmount?.string ?? "0")),
            new Decimal("0"),
          );

        unearnedRevenue = contractPrice
          .sub(longTermDeferredRevenue!)
          .sub(cumulativeRecognizedRevenue);
        unbilledAccountsReceivables = contractPrice
          .sub(longTermReceivables!)
          .sub(cumulativeInvoiceAmount);

        data.longTermReceivables = longTermReceivables;
        data.longTermDeferredRevenue = longTermDeferredRevenue;
        data.unbilledAccountsReceivables = unbilledAccountsReceivables;
        data.unearnedRevenue = unearnedRevenue;
        data.cumulativeRecognizedRevenue = cumulativeRecognizedRevenue;
        data.cumulativeInvoiceAmount = cumulativeInvoiceAmount;
      }

      return data;
    });

  // Build rebalance rows. A rebalance runs on the 20th of its keyed month (adjustments run on the
  // 1st) and only re-carves deferred revenue between the ST (23200) and LT (28655) buckets. We map
  // its stored balances onto the field names the existing GL columns already read so those column
  // renderers work unchanged, and we DON'T re-thread the running balances of later adjustment rows.
  // Entries are processed in chronological (key) order so each row can reference the previous
  // entry's carve-split effective date for its adjusting-period start.
  const rebalanceRows = Object.entries(fixedFeeConfig.rebalance ?? {})
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([yearMonth, rebalance], index, entries) => {
      const carveSplitDate = formatEffectiveDate(
        rebalance.carveSplitRatioEffectiveDate,
      );
      // Period start = later of the previous rebalance's carve-split date and the deal start date,
      // falling back to the deal start date when there is no preceding rebalance entry.
      const dealStartDate = fixedFeeConfig.dealStartDate ?? "";
      const previousCarveSplitDate =
        index > 0
          ? formatEffectiveDate(
              entries[index - 1][1].carveSplitRatioEffectiveDate,
            )
          : undefined;
      const periodStartDate =
        previousCarveSplitDate &&
        previousCarveSplitDate.localeCompare(dealStartDate) > 0
          ? previousCarveSplitDate
          : dealStartDate;

      return {
        rowType: "rebalance",
        adjustmentDate: carveSplitDate,
        // Sort by the 20th so the rebalance lands after that month's 1st-of-month adjustment.
        sortKey: `${yearMonth}-20`,
        periodStartDate,
        periodEndDate: carveSplitDate,
        rebalanceYearMonth: yearMonth,
        rebalanceRevision: rebalance.revision,
        carveSplitRatioEffectiveDate: rebalance.carveSplitRatioEffectiveDate,
        // 23200 Unearned Revenue column reads `unearnedRevenue`; 28655 LT Deferred Revenue reads
        // `longTermDeferredRevenue`.
        unearnedRevenue: rebalance.deferredRevenue,
        longTermDeferredRevenue: rebalance.longTermDeferredRevenue,
      };
    });

  // Merge adjustments and rebalances into a single chronological timeline (issuance stays first).
  adjustmentData.push(
    ...[...adjustmentRows, ...rebalanceRows].sort((a, b) =>
      a.sortKey.localeCompare(b.sortKey),
    ),
  );

  return (
    <Table
      columnDefinitions={adjustmentColumnDefinitions}
      items={adjustmentData}
      wrapLines={false}
      resizableColumns={false}
      header={
        <Header
          counter={`(${adjustmentCount + rebalanceCount})`}
          actions={
            hasFx ? (
              <SpaceBetween
                direction="horizontal"
                size="xs"
                alignItems="center"
              >
                <Toggle
                  onChange={({ detail }) => setFxApplied(detail.checked)}
                  checked={fxApplied}
                >
                  {`FX (${fixedFeeConfig.contractCurrencyCode} \u2192 ${fixedFeeConfig.billingCurrencyCode}) Applied`}
                </Toggle>
                <Popover
                  triggerType="custom"
                  position="top"
                  size="medium"
                  content={`This is calculated by applying the FX rate (${Number(fixedFeeConfig.fxRate!.string).toFixed(10)}) on the amount directly, might have small discrepancies with actual numbers in some places.`}
                >
                  <Button variant="inline-link" iconName="status-info">
                    Disclaimer
                  </Button>
                </Popover>
              </SpaceBetween>
            ) : undefined
          }
        >
          Adjustments
        </Header>
      }
      stickyColumns={{ first: 5, last: 0 }}
    />
  );
};
